/**
 * Reviewing offline deletion requests: destroy the order, or put it back.
 *
 * Unlike the other cached-order workflows, every deletion arrives here --
 * not only the ones a replay refused. Deletion is the one offline change that
 * cannot be corrected afterwards, so a person decides each time.
 *
 * Rejecting is not a failure. The order returns to view because it was never
 * deleted, only hidden.
 *
 * Loading stays coordinated across the review workflows, so this exposes
 * `fetchItems` for the screen's single loader rather than loading on its own.
 *
 * See TCA-26081406, owner decisions 1 and 2.
 */
import { useState } from 'react';
import { useReviewActions } from './useReviewActions';

import {
  applyCachedOrderDeleteRequest,
  getCachedOrderDeleteRequests,
  resolveCachedOrderDeleteRequest,
} from '@/api/orders';

export function useOfflineDeleteReview({ reload, owner }) {
  const [items, setItems] = useState([]);
  const [resolvingId, setResolvingId] = useState(null);
  const act = useReviewActions(owner, reload);
  const fetchItems = read => read.local(getCachedOrderDeleteRequests);
  const decide = (item, approve) => {
    const mutation = item.mutation || {};
    if (!mutation.id || !window.confirm(approve
      ? `Permanently delete ${item.order?.order_number || item.order?.recipient_name || 'this order'}? This cannot be undone.`
      : 'Keep this order and cancel the deletion request?')) return false;
    return act(item, {
      key: `mutation:${mutation.id}`, start: () => setResolvingId(mutation.id), finish: () => setResolvingId(null),
      cloud: approve ? config => applyCachedOrderDeleteRequest(mutation, config) : undefined,
      validateCloud: value => value?.applied === true,
      validateLocal: value => value?.state === (approve ? 'applied' : 'discarded'),
      local: (config, result) => resolveCachedOrderDeleteRequest(mutation.id,
        approve ? 'use_offline_change' : 'discard_offline_change', result?.cloud_order, config),
      reason: approve ? 'offline_delete_applied' : 'offline_delete_rejected',
      message: approve ? 'Order deleted' : 'Order kept',
      failure: approve ? 'Failed to delete order' : 'Failed to cancel the request',
    });
  };
  return { items, setItems, fetchItems, resolvingId,
    onApprove: item => decide(item, true), onReject: item => decide(item, false) };
}
export default useOfflineDeleteReview;
